import styles from "./UploadImage.module.scss";
import React, { useEffect, useRef, useState } from 'react';
import Button from "../Button/Button";
import Spinner from "../Spinner/Spinner";
import uploadImageHover from "../../../assets/images/LightTheme/upload_image_hover.svg";
import uploadImageHoverDark from "../../../assets/images/DarkTheme/upload_image_hover.svg";
import uploadDocIconLight from "../../../assets/images/LightTheme/upload_img_light.svg";
import uploadDocIconDark from "../../../assets/images/DarkTheme/upload_img_dark.svg";
import { useAppSelector } from "../../../redux/hooks";
import { useTranslation } from "react-i18next";
import {
    addDragDropListeners,
    createDragHandlers,
    type DragHandlers,
    handleFileInputChange,
    removeDragDropListeners,
    readFileAsDataURL, ACCEPT_IMAGES, hasAllowedExtension, MAX_IMAGE_FILE_SIZE, IMAGE_EXTENSIONS
} from "../../../utils/upload";
import Icon from "../Icon/Icon";
import ErrorMessage from "../../../utils/fieldErrorMesssage";
import ImagePreview from "../ImagePreview/ImagePreview";

interface UploadImageProps {
    title?: string;           // dynamic title (default: "Profile Photo")
    cancelText?: string;      // Cancel button label (default: "Change Photo")
    confirmText?: string;     // Confirm button label (default: "Save")
    onCancel?: () => void;     // Cancel handler
    onConfirm: (fileDataUrl: string | null) => void;    // Confirm handler returns selected image as Data URL
    className?: string;       // optional additional container classes
    allowedExtensions?: readonly string[] //option allowed extensions
}

const UploadImage: React.FC<UploadImageProps> = ({
    title,
    cancelText,
    confirmText,
    onCancel,
    onConfirm,
    className,
    allowedExtensions
}) => {
    const { t } = useTranslation();
    const [isHover, setIsHover] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dragDropRef = useRef<HTMLDivElement | null>(null);
    // Use shared max image file size from utils to avoid duplication
    const allowedFileSize = MAX_IMAGE_FILE_SIZE;
    const [isDragOver, setIsDragOver] = useState(false);
    const [selectedFile, setSelectedFile] = useState<any>();
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);

    const isActive = isHover || isDragOver;
    const dragHandlersRef = useRef<DragHandlers | null>(null);

    useEffect(() => {
        const node = dragDropRef.current
        if (node) {
            const handlers = createDragHandlers(setIsDragOver, onFileChange);
            dragHandlersRef.current = handlers;
            addDragDropListeners(node, handlers);
            return () => {
                if (dragHandlersRef.current) {
                    removeDragDropListeners(node, dragHandlersRef.current);
                }
            };
        }
    }, []);

        const onFileChange = async (
            event: { target: { files?: FileList | null } } | React.ChangeEvent<HTMLInputElement>
        ) => {
            const files = 'target' in event ? event.target.files : undefined;
            const file: File | undefined = files?.[0];
        if (!file) return;

        // start loader immediately after selecting a file
        setIsPreviewLoading(true);

        // 1) Extension validation (use allowedExtensions prop if provided, else default from utils)
        if (!hasAllowedExtension(file.name, allowedExtensions ?? IMAGE_EXTENSIONS)) {
            setErrorMsg(t('please_upload_valid_image_type'));
            setSelectedFile(undefined);
            setIsPreviewLoading(false);
            if (inputRef.current) inputRef.current.value = '';
            return;
        }

        // 2) If size exceeds limit, still read & preview, but keep error and disable Save via errorMsg
        if (file.size > allowedFileSize) {
            try {
                const dataUrl = await readFileAsDataURL(file);
                setSelectedFile({ file: dataUrl, imgFile: file });
                setErrorMsg(t('image_should_be_at_least_15_mb'));
                // Keep loader on until <img onLoad> fires
            } catch (e) {
                setErrorMsg(t('please_upload_valid_image_type'));
                setSelectedFile(undefined);
                setIsPreviewLoading(false);
            }
            return;
        }

        // 3) Valid size: delegate to util, clear error on success
        handleFileInputChange(
            event,
            allowedFileSize,
            (selected) => {
                setSelectedFile(selected);
                setErrorMsg(null);
            },
            () => {
                setErrorMsg(t('image_should_be_at_least_15_mb'));
                setIsPreviewLoading(false);
            },
            () => { setIsPreviewLoading(false); }
        );
    };

    const handleClick = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    const handleRemoveImage = () => {
        setSelectedFile(undefined);
        setIsHover(false);
        setIsDragOver(false);
        setErrorMsg(null);
        setIsPreviewLoading(false);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);

    const iconSrc = currentTheme === "light"
        ? (isActive ? uploadImageHover : uploadDocIconLight)
        : (isActive ? uploadImageHoverDark : uploadDocIconDark);

    // Disabled states per requirement
    const isChangeDisabled = isPreviewLoading || !selectedFile?.file;
    const isSaveDisabled = isPreviewLoading || !selectedFile?.file || !!errorMsg;

    // i18n fallbacks for optional props
    const i18nTitle = title ?? t("profile_photo");
    const i18nCancel = cancelText ?? t("change_photo");
    const i18nConfirm = confirmText ?? t("btnSave");


    return (
        <div className={`${styles.upload_container} ${className ?? ""}`}>
            <div className={`${styles.modal_title}`}>{i18nTitle}</div>

            <div
                className={`${styles.upload_wrapper}`}
                ref={dragDropRef}
            >
                <input
                    type="file"
                    onChange={onFileChange}
                    style={{ display: "none" }}
                    ref={inputRef}
                    accept={ACCEPT_IMAGES}
                />
                {selectedFile?.file ? (
                    <div className={`${styles.preview_container}`}>
                        <div className={styles.img_container}>
                            <ImagePreview
                                src={String(selectedFile.file)}
                                alt={t("upload")}
                                className={styles.preview_img}
                                onLoad={() => setIsPreviewLoading(false)}
                                onError={() => { setIsPreviewLoading(false); setErrorMsg(t('please_upload_valid_image_type')); setSelectedFile(undefined); }}
                                style={{ visibility: isPreviewLoading ? 'hidden' : 'visible' }}
                            />
                            {isPreviewLoading && (
                                <div className={styles.loader_overlay}>
                                    <Spinner loadingSize="large" />
                                </div>
                            )}
                            <div className={styles.crop_mask} />
                        </div>
                        <div className={`${styles.remove_section}`}>
                            <div
                                className={`${styles.remove_button} ${isPreviewLoading ? styles.remove_button_disabled : ""}`}
                                onClick={handleRemoveImage}
                            >
                                <div><Icon name="delete_icon_bin" size={16} color={isPreviewLoading ? 'var(--text-text-disabled)' : 'var(--text-text-brand, #99B2F5)'} /></div>
                                <div className={`${styles.remove_text}`}>{t('remove_image')}</div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        onMouseEnter={() => setIsHover(true)}
                        onMouseLeave={() => setIsHover(false)}
                        className={`${styles.upload_content} ${isDragOver ? `${styles.uploading_state}` : ""}`}
                        style={{ position: "relative" }}>
                        {isPreviewLoading && (
                            <div className={styles.loader_overlay} aria-hidden>
                                <Spinner loadingSize="large" />
                            </div>
                        )}
                        <div className={styles.icon_wrapper}>
                            <img src={iconSrc} alt={t("upload")} />
                        </div>
                        <div className={`${styles.upload_text_container}`}>
                            <div>{t("dragNDropMsg")}</div>
                            <div>{t("or")}</div>
                            <div className={`${styles.upload_text} ${styles.upload_text_to_select}`} onClick={handleClick}>
                                {t("chooseFromLocalDevice")}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className={`${styles.requirements_section}`}>
                {t('upload_requirements_text')}
            </div>

            {errorMsg && (
                <ErrorMessage message={errorMsg} className={styles.error_message} />
            )}

            <div className={styles.modal_actions}>
                <Button
                    id="noCancelButton"
                    onClick={onCancel || handleClick}
                    label={i18nCancel}
                    style="unfilled"
                    className={`${styles.btn}`}
                    isDisabled={isChangeDisabled}
                    disabledThemeColor={"var(--text-text-disabled)"}
                    customStyle={isChangeDisabled ? { border: '2px solid var(--text-text-disabled)',backgroundColor: 'inherit' } : undefined}
                />
                <Button
                    id="yesCancelButton"
                    onClick={() => onConfirm(selectedFile?.file ? String(selectedFile.file) : null)}
                    label={i18nConfirm}
                    style="filled"
                    className={`${styles.btn}`}
                    isDisabled={isSaveDisabled}
                    disabledThemeColor={"var(--text-text-disabled)"}
                />
            </div>
        </div>
    );
};

export default UploadImage;